import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  PLATFORM_OPERATOR_CONTEXT,
  type HeldRoot,
  type OperatorContextSelection,
} from './operatorContext.contract';
import { logout } from '../session/logout';
import type { RootState } from '../store';

/** Per-tab, so two tabs can work in two organizations at once. */
const SELECTION_KEY = 'backrs.operatorContext';

const HELD_ROOTS_KEY = 'backrs.operatorContext.heldRoots';

export type OperatorContextState = Readonly<{
  institutionId: OperatorContextSelection | null;
  ownerUserId: number | null;
  /**
   * The wire offers the roots only when it refuses, and a tab reading under a root it still
   * holds is not refused again.
   */
  heldRoots: readonly HeldRoot[];
}>;

const NO_CONTEXT: OperatorContextState = {
  institutionId: null,
  ownerUserId: null,
  heldRoots: [],
};

const storedId = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : null;

const storedSelection = (value: unknown): OperatorContextSelection | null =>
  value === PLATFORM_OPERATOR_CONTEXT ? PLATFORM_OPERATOR_CONTEXT : storedId(value);

const parseStored = (key: string): unknown => {
  try {
    const raw = window.sessionStorage.getItem(key);

    return raw === null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown): void => {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* a tab that cannot persist still holds its selection for its own lifetime */
  }
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readStoredHeldRoots = (): readonly HeldRoot[] => {
  const parsed = parseStored(HELD_ROOTS_KEY);

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((entry) => {
      const root = asRecord(entry);
      const id = storedId(root?.id);

      return id === null ? null : { id, name: typeof root?.name === 'string' ? root.name : null };
    })
    .filter((root): root is HeldRoot => root !== null);
};

/** Anything unreadable reads as no selection. */
export const readStoredOperatorContext = (): OperatorContextState => {
  const record = asRecord(parseStored(SELECTION_KEY));
  const institutionId = storedSelection(record?.institutionId);
  const heldRoots = readStoredHeldRoots();

  if (institutionId === null) return { ...NO_CONTEXT, heldRoots };

  return { institutionId, ownerUserId: storedId(record?.ownerUserId), heldRoots };
};

export const clearStoredOperatorContext = (): void => {
  write(SELECTION_KEY, null);
  write(HELD_ROOTS_KEY, null);
};

export const operatorContextSlice = createSlice({
  name: 'operatorContext',
  initialState: readStoredOperatorContext(),
  reducers: {
    operatorContextSet: (state, action: PayloadAction<OperatorContextState>) => ({
      ...state,
      ...action.payload,
    }),
    operatorRootsOffered: (state, action: PayloadAction<readonly HeldRoot[]>) => ({
      ...state,
      heldRoots: action.payload,
    }),
    operatorContextCleared: () => NO_CONTEXT,
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => NO_CONTEXT);
  },
});

const { operatorContextSet, operatorRootsOffered } = operatorContextSlice.actions;

export const { operatorContextCleared } = operatorContextSlice.actions;

/** The two drifting apart survives a reload. */
export const commitOperatorContext = (next: OperatorContextState) => {
  write(SELECTION_KEY, {
    institutionId: next.institutionId,
    ownerUserId: next.ownerUserId,
  });
  write(HELD_ROOTS_KEY, next.heldRoots);

  return operatorContextSet(next);
};

export const commitHeldRoots = (heldRoots: readonly HeldRoot[]) => {
  write(HELD_ROOTS_KEY, heldRoots);

  return operatorRootsOffered(heldRoots);
};

/** The same account and the same roots must land on the same organization every time. */
export const lowestHeldRoot = (heldRoots: readonly HeldRoot[]): HeldRoot | null =>
  heldRoots.reduce<HeldRoot | null>(
    (lowest, root) => (lowest === null || root.id < lowest.id ? root : lowest),
    null,
  );

/**
 * The operator context to send, or null to send none. A selection is only this
 * session's to send when the account that made it is the one signed in: logging
 * in as someone else in the same tab must not carry the previous operator's
 * reach into their requests.
 */
export const selectOperatorInstitutionId = (state: RootState): OperatorContextSelection | null => {
  const { institutionId, ownerUserId } = state.operatorContext;

  if (institutionId === null) return null;

  const currentUserId = state.user.user?.id ?? null;

  return currentUserId !== null && currentUserId !== ownerUserId ? null : institutionId;
};

/** The raw selection, whoever made it. */
export const selectStoredOperatorInstitutionId = (
  state: RootState,
): OperatorContextSelection | null => state.operatorContext.institutionId;

/** The stored selection where it names a root, and null where it is platform mode. For the
 * surfaces that name one of the caller's own institutions, which platform mode is not. */
export const selectStoredOperatorRootId = (state: RootState): number | null => {
  const stored = state.operatorContext.institutionId;

  return typeof stored === 'number' ? stored : null;
};

/** Platform mode is read from the selection the requests carry, never from a read that
 * describes it: a read that fails must not make an active mode look inactive. */
export const selectIsPlatformContext = (state: RootState): boolean =>
  selectOperatorInstitutionId(state) === PLATFORM_OPERATOR_CONTEXT;

/** The operator a tab is reading under, or null where it names none — platform mode included. */
export const selectOperatingAsRootId = (state: RootState): number | null => {
  const selection = selectOperatorInstitutionId(state);

  return typeof selection === 'number' ? selection : null;
};

export const selectHeldRoots = (state: RootState): readonly HeldRoot[] =>
  state.operatorContext.heldRoots;
